// src/components/screens/FeedbackForm.js
import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { postFunctionJson } from "../utils/functionHttp";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

// ✅ Cloud Function URL for sampleFeedback
const FEEDBACK_FUNCTION_URL =
  "https://us-central1-deliveryapp-c1bf2.cloudfunctions.net/sampleFeedback";

export default function FeedbackForm() {
  const query = useQuery();
  const companyId = query.get("companyId");
  const requestId = query.get("requestId");

  const [rating, setRating] = useState(0);
  const [comments, setComments] = useState("");
  const [useCase, setUseCase] = useState("");
  const [interestLevel, setInterestLevel] = useState("warm");
  const [nextStep, setNextStep] = useState("follow_up");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const isMissingParams = !companyId || !requestId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (isMissingParams) {
      setError("Missing link information. Please contact your rep.");
      return;
    }

    if (!rating) {
      setError("Please select a rating.");
      return;
    }

    try {
      setSubmitting(true);

      const res = await postFunctionJson(FEEDBACK_FUNCTION_URL, {
        body: {
          companyId,
          requestId,
          rating,
          comments,
          useCase,
          interestLevel,
          nextStep,
        },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Something went wrong");
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Feedback submit error", err);
      setError(err.message || "Unable to submit feedback.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white shadow-sm rounded-xl p-6 border border-slate-200">
          <h1 className="text-xl font-semibold text-slate-900 mb-2">
            Thank you!
          </h1>
          <p className="text-sm text-slate-600">
            Your feedback has been recorded. We appreciate you taking the time
            to help us improve.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full bg-white shadow-sm rounded-xl p-6 border border-slate-200">
        <h1 className="text-xl font-semibold text-slate-900 mb-2">
          How did your sample go?
        </h1>
        <p className="text-sm text-slate-600 mb-4">
          This quick form helps your distributor know how the sample performed and what should happen next.
        </p>

        {isMissingParams && (
          <div className="mb-4 text-sm text-red-600">
            This link is missing some information. Please contact your rep so
            they can resend it.
          </div>
        )}

        {error && (
          <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Overall, how did the sample perform?
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRating(value)}
                  className={`w-10 h-10 rounded-full border text-sm font-medium ${
                    rating === value
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {value}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-slate-500">
              1 = Very poor, 5 = Excellent
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Kitchen reaction
              </label>
              <select
                value={interestLevel}
                onChange={(e) => setInterestLevel(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="hot">Strong interest</option>
                <option value="warm">Interested with tweaks</option>
                <option value="cold">Not a fit right now</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Best next step
              </label>
              <select
                value={nextStep}
                onChange={(e) => setNextStep(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="follow_up">Have my rep follow up</option>
                <option value="quote">Send pricing / order info</option>
                <option value="resample">Try a revised sample</option>
                <option value="no_action">No action needed</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              How did you use it? <span className="text-slate-400">(optional)</span>
            </label>
            <input
              type="text"
              value={useCase}
              onChange={(e) => setUseCase(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Example: staff tasting, LTO test, brunch special..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Anything you&apos;d like to share?{" "}
              <span className="text-slate-400">(optional)</span>
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={4}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Flavor, performance, customer reaction, ideas for next time..."
            />
          </div>

          <button
            type="submit"
            disabled={submitting || isMissingParams}
            className={`w-full inline-flex justify-center items-center px-4 py-2 rounded-lg text-sm font-medium ${
              submitting || isMissingParams
                ? "bg-slate-300 text-slate-600 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            {submitting ? "Submitting..." : "Submit feedback"}
          </button>

          <p className="text-[11px] text-slate-400 mt-1">
            By submitting, you agree your response may be shared with your
            distributor&apos;s team.
          </p>
        </form>
      </div>
    </div>
  );
}
